import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  LinearProgress,
  Chip,
  Icon,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useAppSelector } from "../../Redux/Store/Hooks";

const QuestionBankStatus = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: listQuestionbank } = useAppSelector(
    (state) => state.ListQuestionBankSlice
  );
  const Questionbank: any[] = (listQuestionbank?.data ?? []).map(
    (user: any, index: number) => ({
      ...user,
      index: index + 1,
    })
  );

  const total = Questionbank?.reduce(
    (sum, item) => sum + item.total_questions,
    0
  );

  return (
    <Card
      sx={{
        marginTop: "20px",
        width: "100%",
        boxShadow: "0px 2px 12px rgba(0,0,0,0.05)",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        p="30px"
        sx={{
          padding: "20px 20px 20px 20px",
          flexWrap:"wrap",
          gap:"0px 15px"
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "19px" }}>
          Question Bank Status
        </Typography>
        <Typography
          sx={{ cursor: "pointer", color: "#6b7280", fontSize: "15px",marginTop:"5px" }}
        >
          {total} total questions &nbsp;
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": {
            display: "block",
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#b9b9b9",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#919191",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
          },
        }}
      >
        <Card
          sx={{
            width: {
              md: "100%", // 320px for medium screens
              lg: "100%", // 100% for large and above
              xl: "100%",
            },
          }}
        >
          <Box
            display="flex"
            fontWeight="bold"
            borderTop={"1px solid #f0f0f0"}
            borderBottom={"1px solid #f0f0f0"}
            px={2}
            py={1}
            color="#6b7280"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                SUBJECT AREA
              </Grid>
              <Grid item xs={12} sm={2.25}>
                ATTEMPTED
              </Grid>
              <Grid item xs={6} sm={2.25}>
                ACCURACY
              </Grid>
              <Grid item xs={6} sm={2.25}>
                AVG. TIME
              </Grid>
              <Grid item xs={6} sm={2.25}>
                SUGGESTED ACTION
              </Grid>
            </Grid>
          </Box>

          {Questionbank?.length > 0 ? (
            Questionbank?.map((subject, index) => (
              <Box
                key={index}
                px={2}
                py={1}
                borderTop={index !== 0 ? "1px solid #f0f0f0" : "none"}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography fontWeight={600} sx={{ fontSize: "15px" }}>
                      {subject.questiontype_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" >
                      {subject.total_questions} questions
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={2.25}>
                    <Box display="flex" alignItems="center">
                      <LinearProgress
                        variant="determinate"
                        value={
                          (subject.attempted_questions /
                            subject.total_questions) *
                          100
                        }
                        sx={{
                          height: 6,
                          width: "100%",
                          borderRadius: "10px",
                          backgroundColor: "#e0e0e0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#2196f3",
                          },
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "13px"}}>
                        {subject.attempted_questions}/{subject.total_questions}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={2.25}>
                    <Chip
                      label={`${subject.accuracy_percentage}%`}
                      size="small"
                      sx={{
                        backgroundColor:
                          subject.accuracy_percentage >= 75
                            ? "#d9fbe3"
                            : subject.accuracy_percentage >= 50
                            ? "#fef9c3"
                            : "#fee2e2",
                        color:
                          subject.accuracy_percentage >= 75
                            ? "green"
                            : subject.accuracy_percentage >= 50
                            ? "#854d0e"
                            : "#991b1b",
                        fontWeight: 600,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2.25}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "14px"}}>
                      {subject.avg_time_taken_seconds != null
                        ? `${(subject.avg_time_taken_seconds / 60).toFixed(1)} min/q`
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={2.25}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color:
                            subject.accuracy_percentage >= 75
                              ? "#16a34a"
                              : subject.accuracy_percentage >= 50
                              ? "#d97706"
                              : "#dc2626",
                          fontWeight: 400,
                          fontSize: "14px",
                        }}
                      >
                        {subject.accuracy_percentage >= 75 ? (
                          <CheckCircleOutlineIcon fontSize="small" />
                        ) : subject.accuracy_percentage >= 50 ? (
                          <AccessTimeIcon fontSize="small" />
                        ) : (
                          <ErrorOutlineIcon fontSize="small" />
                        )}
                        &nbsp;
                        {subject.accuracy_percentage >= 75
                          ? "Continue"
                          : subject.accuracy_percentage >= 50
                          ? "Practice More"
                          : "Needs Review"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Box px={2} py={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                No data found
              </Typography>
            </Box>
          )}
        </Card>
      </Box>
    </Card>
  );
};

export default QuestionBankStatus;
